/**
 * Linked Color utilities for CSS math expressions and reference color management
 * Supports Evil Martians-style color manipulation syntax
 */

import { parseColor, getRelativeChroma, applyRelativeChroma, getMaxChromaForWorkspace } from './colorUtils.js';
import { measureContrastPair, colorInputForMeasurement, formatOklchForMeasure, roundContrastLevel } from './wcagContrast.js';

/**
 * Parse CSS math expression like "+0.1", "*0.5", "*0.5+0.1", etc.
 * @param {string} expression - CSS math expression
 * @param {number} baseValue - Original value to apply expression to
 * @param {string} component - Component type ('l', 'c', 'h', 'a') for context
 * @param {Object} referenceOklch - Reference OKLCH color for relative chroma calculations
 * @returns {number} Calculated result
 */
export function parseColorMathExpression(expression, baseValue, component = null, referenceOklch = null, newOklch = null, workspaceGamut = 'p3') {
  if (!expression || typeof expression !== 'string') {
    return baseValue;
  }

  const trimmed = expression.trim();
  
  // Handle relative chroma ONLY when the expression contains a percentage
  // Otherwise, treat as numeric math on the reference chroma
  if (component === 'c' && referenceOklch && /%/.test(trimmed)) {
    return parseRelativeChromaExpression(trimmed, baseValue, referenceOklch, newOklch, workspaceGamut);
  }
  
  // Handle percentage-based lightness adjustments (e.g., "+10%", "-20%")
  // These represent percentage of available range, not absolute values
  if (component === 'l' && /%/.test(trimmed)) {
    const percentageMatch = trimmed.match(/^([+\-])(\d*\.?\d+)%$/);
    if (percentageMatch) {
      const sign = percentageMatch[1];
      const percentage = parseFloat(percentageMatch[2]);
      
      if (!isNaN(percentage) && percentage >= 0 && percentage <= 100) {
        const p = percentage / 100; // Convert to 0-1 range
        
        if (sign === '+') {
          // Lighter: L + (1 - L) * p = L + p - p*L = (1-p)*L + p
          // Convert to formula: * (1-p) + p
          const multiplier = 1 - p;
          const addend = p;
          // Apply the formula: result = baseValue * multiplier + addend
          return baseValue * multiplier + addend;
        } else {
          // Darker: L - L * p = L * (1 - p)
          // Convert to formula: * (1-p)
          const multiplier = 1 - p;
          // Apply the formula: result = baseValue * multiplier
          return baseValue * multiplier;
        }
      }
    }
    // If percentage pattern doesn't match expected format, fall through to regular parsing
  }
  
  // If it's just a number, return it directly
  if (/^\d*\.?\d+$/.test(trimmed)) {
    return parseFloat(trimmed);
  }

  // Parse complex expressions like "*0.5+0.1" or "+0.1*0.8"
  let result = baseValue;
  
  // Split by operators while keeping them
  const tokens = trimmed.split(/([+\-*/])/).filter(token => token.trim());
  
  let currentOperator = null;
  
  for (const token of tokens) {
    const clean = token.trim();
    
    if (['+', '-', '*', '/'].includes(clean)) {
      currentOperator = clean;
    } else if (/^\d*\.?\d+$/.test(clean)) {
      const value = parseFloat(clean);
      
      if (currentOperator === '+') {
        result += value;
      } else if (currentOperator === '-') {
        result -= value;
      } else if (currentOperator === '*') {
        result *= value;
      } else if (currentOperator === '/') {
        result /= value;
      } else {
        // First token without operator, treat as direct value
        result = value;
      }
    }
  }
  
  return result;
}

/**
 * Parse relative chroma expression for C component
 * @param {string} expression - Expression like "70%" (simple percentage only)
 * @param {number} baseValue - Base chroma value
 * @param {Object} referenceOklch - Reference OKLCH color
 * @param {Object} newOklch - New OKLCH values (L, H, A) for relative chroma calculation
 * @returns {number} Calculated chroma value
 */
function parseRelativeChromaExpression(expression, baseValue, referenceOklch, newOklch = null, workspaceGamut = 'p3') {
  const trimmed = expression.trim();
  
  // Use new L, H, A values if provided, otherwise fall back to reference
  const targetL = newOklch != null && newOklch.l != null ? newOklch.l : referenceOklch.l;
  const targetH = newOklch != null && newOklch.h != null ? newOklch.h : referenceOklch.h;
  const targetA = newOklch != null && newOklch.alpha != null ? newOklch.alpha : referenceOklch.alpha;
  
  // Handle simple percentage values (relative chroma)
  if (trimmed.endsWith('%')) {
    const percentage = parseFloat(trimmed);
    if (isNaN(percentage)) return baseValue;
    
    // Convert percentage to relative chroma (0-1)
    const relativeChroma = Math.max(0, Math.min(1, percentage / 100));
    
    // Get maximum chroma for the target L and H values in the workspace gamut
    const maxChroma = getMaxChromaForWorkspace(targetL, targetH, targetA, workspaceGamut);
    
    // Apply relative chroma
    return maxChroma * relativeChroma;
  }
  
  // Fall back to regular number parsing
  return parseFloat(trimmed) || baseValue;
}

/**
 * Clamp value to valid range based on component
 * @param {number} value - Value to clamp
 * @param {string} component - 'l', 'c', 'h', or 'a'
 * @returns {number} Clamped value
 */
export function clampColorComponent(value, component) {
  switch (component.toLowerCase()) {
    case 'l': // Lightness: 0-1
    case 'a': // Alpha: 0-1
      return Math.max(0, Math.min(1, value));
    
    case 'c': // Chroma: 0-0.37 (max in OKLCH)
      return Math.max(0, Math.min(0.37, value));
    
    case 'h': // Hue: 0-360 degrees (wrap around)
      return ((value % 360) + 360) % 360;
    
    default:
      return value;
  }
}



/**
 * Filter available reference colors based on current context
 * @param {Array} allColors - All available colors (primitives + tokens)
 * @param {string} currentColorId - ID of current color to exclude
 * @param {boolean} isToken - Whether current color is a token (affects filtering)
 * @returns {Array} Filtered color options
 */
export function getAvailableReferenceColors(allColors, currentColorId, isToken = false, workspaceGamut = 'p3') {
  if (!Array.isArray(allColors)) {
    return [];
  }

  return allColors.filter(color => {
    // Exclude current color
    if (color.id === currentColorId) {
      return false;
    }

    // For primitives: only allow other primitives
    if (!isToken) {
      return color.type === 'primitive' || (!color.type && !color.isToken);
    }

    // For tokens: allow both primitives and other tokens
    return true;
  }).map(color => {
    let finalColor = '#808080'; // fallback
    let folderName = null;
    let folderId = null;
    
    if (color.mode === 'linked' && color.referenceColorId) {
      // Skip expensive calculation - use placeholder color
      // Actual color will be provided by precomputed colors in dropdown
      const computedColor = { isValid: true, hex: '#808080' };
      if (computedColor.hex) {
        finalColor = computedColor.hex;
      }
    } else if (color.mode === 'contrast' && color.backgroundColorId && color.foregroundColorId) {
      // For contrast-based colors, calculate the actual color value (same as CardColor)
      try {
        // Prevent circular dependency by filtering out the current color from availableColors
        const filteredColors = allColors.filter(c => c.id !== color.id);
        
        
        // Skip expensive calculation - use placeholder color
        // Actual color will be provided by precomputed colors in dropdown
        const computedColor = { isValid: true, hex: '#808080' };
        if (computedColor && computedColor.isValid) {
          // Use hex value for better CSS compatibility in dropdowns
          if (computedColor.hex) {
            finalColor = computedColor.hex;
          } else if (computedColor.oklch) {
            const { l, c, h, alpha = 1 } = computedColor.oklch;
            if (alpha < 1) {
              finalColor = `oklch(${l.toFixed(3)} ${c.toFixed(3)} ${Math.round(h)} / ${alpha.toFixed(3)})`;
            } else {
              finalColor = `oklch(${l.toFixed(3)} ${c.toFixed(3)} ${Math.round(h)})`;
            }
          }
          
        }
      } catch (error) {
        // Use fallback color
      }
    } else {
      // For static colors, parse the direct color value (prefer OKLCH to preserve hue)
      const colorValue = color.colorValue || color.hexValue || '#808080';
      const parsedColor = parseColor(colorValue, workspaceGamut);
      
      // Use hex value for better CSS compatibility in dropdowns
      if (parsedColor.isValid) {
        if (parsedColor.hex) {
          finalColor = parsedColor.hex;
        } else if (parsedColor.oklch) {
          const { l, c, h, alpha = 1 } = parsedColor.oklch;
          if (alpha < 1) {
            finalColor = `oklch(${l.toFixed(3)} ${c.toFixed(3)} ${Math.round(h || 0)} / ${alpha.toFixed(3)})`;
          } else {
            finalColor = `oklch(${l.toFixed(3)} ${c.toFixed(3)} ${Math.round(h || 0)})`;
          }
        }
      } else {
        finalColor = colorValue;
      }
    }
    
    // Get folder information if available
    if (color.folderId) {
      folderId = color.folderId;
      // Use folderPath if available, otherwise fall back to folderName
      folderName = color.folderPath || color.folderName || 'Unknown Folder';
    }
    
    // Just use the color name (folder path is shown in folder headers)
    let label = color.name;
    
    return {
      value: color.id,
      label: label,
      color: finalColor,
      isToken: color.isToken || false,
      oklch: color.oklchValue,
      folderId: folderId,
      folderName: folderName,
      type: color.type || (color.isToken ? 'token' : 'primitive'),
      // Preserve custom section to allow specialized dropdown grouping (e.g. waitlist)
      section: color.section || undefined
    };
  });
}

/**
 * Calculate the final color for a linked color based on reference color and expressions
 * @param {Array|Map} allColors - All available colors array or Map<id, color>
 * @param {string} referenceColorId - ID of the reference color
 * @param {string} lExpression - Lightness expression (e.g., "/2", "+0.1")
 * @param {string} cExpression - Chroma expression
 * @param {string} hExpression - Hue expression
 * @param {string} aExpression - Alpha expression
 * @param {number} _depth - Recursion depth (internal)
 * @param {'apca' | 'wcag'} contrastAlgorithm - Global contrast metric for nested contrast colors
 * @param {'srgb' | 'p3'} colorSpace - Workspace color space used for APCA measurement of nested contrast colors
 * @returns {Object} Calculated color object with isValid, hex, oklch properties
 */
export function calculateLinkedColor(allColors, referenceColorId, lExpression, cExpression, hExpression, aExpression, _depth = 0, contrastAlgorithm = 'apca', colorSpace = 'p3', resolvedById = null) {
  // Prevent infinite recursion
  if (_depth > 10) {
    return { isValid: false, hex: '#808080', oklch: null };
  }
  if (!referenceColorId || !allColors) {
    return { isValid: false, hex: '#808080', oklch: null };
  }

  if (resolvedById && resolvedById.has(referenceColorId)) {
    const resolved = resolvedById.get(referenceColorId);
    if (resolved?.isValid && resolved.oklch) {
      return applyLinkedExpressions(resolved, lExpression, cExpression, hExpression, aExpression, colorSpace);
    }
  }

  const refColor = lookupColor(allColors, referenceColorId);
  if (!refColor) {
    return { isValid: false, hex: '#808080', oklch: null };
  }

  const refColorData = resolveColorData(allColors, refColor, _depth, contrastAlgorithm, colorSpace, resolvedById);
  return applyLinkedExpressions(refColorData, lExpression, cExpression, hExpression, aExpression, colorSpace);
}

function invalidColor() {
  return { isValid: false, hex: '#808080', oklch: null };
}

function lookupColor(allColors, id) {
  if (!id || !allColors) return null;
  if (allColors instanceof Map) return allColors.get(id) || null;
  if (Array.isArray(allColors)) return allColors.find((color) => color.id === id) || null;
  return null;
}

function resolveColorData(allColors, color, depth, contrastAlgorithm, colorSpace, resolvedById) {
  if (!color) return invalidColor();
  if (resolvedById && color.id && resolvedById.has(color.id)) {
    return resolvedById.get(color.id);
  }
  if (color.mode === 'linked' && color.referenceColorId) {
    return calculateLinkedColor(
      allColors,
      color.referenceColorId,
      color.lExpression,
      color.cExpression,
      color.hExpression,
      color.aExpression,
      depth + 1,
      contrastAlgorithm,
      colorSpace,
      resolvedById
    );
  }
  if (color.mode === 'contrast' && color.backgroundColorId && color.foregroundColorId) {
    return calculateContrastColor(
      allColors,
      color.backgroundColorId,
      color.foregroundColorId,
      color.contrastLevel || 60,
      color.useAlpha || false,
      contrastAlgorithm,
      colorSpace,
      resolvedById,
      depth + 1
    );
  }
  return parseColor(color.colorValue || color.hexValue || '#808080', colorSpace);
}

function applyLinkedExpressions(refColorData, lExpression, cExpression, hExpression, aExpression, colorSpace) {
  if (!refColorData?.isValid || !refColorData.oklch) return invalidColor();

  const refOklch = refColorData.oklch;

  const newL = lExpression
    ? clampColorComponent(parseColorMathExpression(lExpression, refOklch.l, 'l', null, null, colorSpace), 'l')
    : refOklch.l;

  let newH = hExpression
    ? clampColorComponent(parseColorMathExpression(hExpression, refOklch.h || 0, 'h', null, null, colorSpace), 'h')
    : (refOklch.h || 0);

  const newA = aExpression
    ? clampColorComponent(parseColorMathExpression(aExpression, refOklch.alpha || 1, 'a', null, null, colorSpace), 'a')
    : (refOklch.alpha || 1);

  let newC;
  if (cExpression) {
    if (cExpression.trim().endsWith('%')) {
      const percentage = parseFloat(cExpression.trim());
      const relativeChroma = Math.max(0, Math.min(1, percentage / 100));
      const maxChroma = getMaxChromaForWorkspace(newL, newH, newA, colorSpace);
      newC = clampColorComponent(maxChroma * relativeChroma, 'c');
    } else {
      const newOklch = { l: newL, h: newH, alpha: newA };
      newC = clampColorComponent(parseColorMathExpression(cExpression, refOklch.c, 'c', refOklch, newOklch, colorSpace), 'c');
    }
  } else if (lExpression || hExpression || aExpression) {
    const relativeChroma = getRelativeChroma(refOklch, colorSpace);
    newC = clampColorComponent(applyRelativeChroma(relativeChroma, newL, newH, newA, colorSpace), 'c');
  } else {
    newC = refOklch.c;
  }

  const isExtremeLight = newL >= 0.98;
  const isExtremeDark = newL <= 0.02;
  if ((isExtremeLight || isExtremeDark) && !hExpression) {
    newH = refOklch.h || 0;
  }

  const finalOklch = { mode: 'oklch', l: newL, c: newC, h: newH, alpha: newA };
  const finalColor = parseColor(formatOklchForMeasure(finalOklch), colorSpace);

  if (finalColor.isValid && finalColor.oklch && (isExtremeLight || isExtremeDark)) {
    finalColor.oklch.h = newH;
  }
  return finalizeAlpha(finalColor, newA);
}

function finalizeAlpha(parsedColor, alpha) {
  if (!parsedColor?.isValid) return parsedColor;
  parsedColor.alpha = alpha;
  if (parsedColor.oklch) parsedColor.oklch.alpha = alpha;
  if (parsedColor.rgb) parsedColor.rgb.alpha = alpha;
  return parsedColor;
}


/**
 * Binary search for the lightness whose exported-channel contrast is at or
 * above target, with the smallest positive overage.
 */
function findOptimalLightness(backgroundColorString, targetContrast, chroma, hue, minL, maxL, useRelativeChroma, preferLighter, measureContrast, workspaceGamut) {
  const tolerance = 1e-7;
  const measure = measureContrast;
  const maxIterations = 96;

  if (maxL - minL < 0.01) {
    if (preferLighter) {
      maxL = Math.min(0.95, minL + 0.5);
    } else {
      minL = Math.max(0.05, maxL - 0.5);
    }
  }

  let bestOverLightness = null;
  let bestOverDifference = Infinity;

  if (preferLighter === null) {
    const whiteContrast = measure("#ffffff", backgroundColorString);
    const blackContrast = measure("#000000", backgroundColorString);
    preferLighter = whiteContrast > blackContrast;
  }

  for (let i = 0; i < maxIterations && maxL - minL > tolerance; i++) {
    const testLightness = (minL + maxL) / 2;
    let actualChroma = chroma;
    if (useRelativeChroma) {
      actualChroma = applyRelativeChroma(chroma, testLightness, hue, 1, workspaceGamut);
    }
    const testColorString = `oklch(${testLightness} ${actualChroma} ${hue})`;
    let testContrast;
    try {
      testContrast = measure(testColorString, backgroundColorString);
      if (!isFinite(testContrast)) testContrast = -Infinity;
    } catch (e) {
      testContrast = -Infinity;
    }

    const overTarget = testContrast - targetContrast;
    if (testContrast >= targetContrast && overTarget < bestOverDifference) {
      bestOverDifference = overTarget;
      bestOverLightness = testLightness;
    }

    if (!isFinite(testContrast) || testContrast < targetContrast) {
      if (preferLighter) minL = testLightness;
      else maxL = testLightness;
    } else {
      if (preferLighter) maxL = testLightness;
      else minL = testLightness;
    }
  }

  return bestOverLightness;
}

/** Smallest 8-bit alpha whose exported-channel contrast is >= target, or null. */
function findOptimalAlpha(fgL, fgC, fgH, bgMeasureString, targetContrast, measure) {
  for (let i = 1; i <= 255; i++) {
    const a = i / 255;
    const testColorString = `oklch(${fgL} ${fgC} ${fgH} / ${a})`;
    let testContrast;
    try {
      testContrast = measure(testColorString, bgMeasureString);
    } catch (e) {
      continue;
    }
    if (isFinite(testContrast) && testContrast >= targetContrast) {
      return a;
    }
  }
  return null;
}

function emitAlphaColor(fgL, fgC, fgH, alpha, bgMeasureString, targetContrast, measure, colorSpace) {
  let a = alpha;
  for (let n = 0; n < 32; n++) {
    const finalOklch = { mode: 'oklch', l: fgL, c: fgC, h: fgH, alpha: a };
    const parsed = parseColor(formatOklchForMeasure(finalOklch), colorSpace);
    if (!parsed?.isValid) break;
    finalizeAlpha(parsed, a);
    parsed.originalInput = formatOklchForMeasure(finalOklch);
    const input = colorInputForMeasurement(parsed, colorSpace);
    if (measure(input, bgMeasureString) >= targetContrast) {
      return parsed;
    }
    a = Math.min(1, a + 1 / 255);
  }
  return null;
}

function emitLightnessColor(lightness, relativeChroma, hue, bgMeasureString, targetContrast, preferLighter, measure, colorSpace, contrastAlgorithm) {
  const towardMore = preferLighter ? 1 : -1;
  const step = 0.0002;
  let L = lightness;
  let found = null;
  for (let i = 0; i < 80; i++) {
    const chroma = applyRelativeChroma(relativeChroma, L, hue, 1, colorSpace);
    const str = formatOklchForMeasure({ l: L, c: chroma, h: hue, alpha: 1 });
    const parsed = parseColor(str, colorSpace);
    if (!parsed?.isValid) break;
    finalizeAlpha(parsed, 1);
    const input = colorInputForMeasurement(parsed, colorSpace);
    const value = measure(input, bgMeasureString);
    if (value >= targetContrast) {
      found = parsed;
      break;
    }
    const next = L + towardMore * step;
    if (next < 0 || next > 1) break;
    L = next;
  }
  if (!found) return null;

  // APCA boards score integer Lc. If we landed at 60.6 (rounds to +1), walk
  // back toward the background until rounded Lc is the target.
  if (contrastAlgorithm === 'apca') {
    let L2 = found.oklch?.l ?? L;
    for (let i = 0; i < 80; i++) {
      const chroma = applyRelativeChroma(relativeChroma, L2, hue, 1, colorSpace);
      const str = formatOklchForMeasure({ l: L2, c: chroma, h: hue, alpha: 1 });
      const parsed = parseColor(str, colorSpace);
      if (!parsed?.isValid) break;
      finalizeAlpha(parsed, 1);
      const input = colorInputForMeasurement(parsed, colorSpace);
      const value = measure(input, bgMeasureString);
      if (value < targetContrast) break;
      found = parsed;
      if (roundContrastLevel(value, 'apca') === roundContrastLevel(targetContrast, 'apca')) {
        break;
      }
      const next = L2 - towardMore * step;
      if (next < 0 || next > 1) break;
      L2 = next;
    }
  }
  return found;
}

function extremeFallback(bgMeasureString, measure, colorSpace) {
  try {
    const whiteContrast = measure("#ffffff", bgMeasureString);
    const blackContrast = measure("#000000", bgMeasureString);
    return parseColor(whiteContrast > blackContrast ? "white" : "black", colorSpace);
  } catch (e) {
    return parseColor("white", colorSpace);
  }
}

/**
 * Calculate contrast-based color: search lightness or 8-bit alpha so the
 * flattened 8-bit pair meets the target APCA Lc / WCAG CR.
 *
 * @param {Array|Map} allColors
 * @param {string} backgroundColorId
 * @param {string} foregroundColorId
 * @param {number} contrastLevel
 * @param {boolean} useAlpha
 * @param {'apca' | 'wcag'} contrastAlgorithm
 * @param {'srgb' | 'p3'} colorSpace
 * @param {Map|null} resolvedById - already-computed colors from the engine cache
 */
export function calculateContrastColor(allColors, backgroundColorId, foregroundColorId, contrastLevel, useAlpha = false, contrastAlgorithm = 'apca', colorSpace = 'p3', resolvedById = null, _depth = 0) {
  if (_depth > 10) return invalidColor();
  if (!backgroundColorId || !foregroundColorId || !allColors || contrastLevel === undefined) {
    return invalidColor();
  }

  const bgColor = lookupColor(allColors, backgroundColorId);
  const fgColor = lookupColor(allColors, foregroundColorId);
  if (!bgColor || !fgColor) return invalidColor();

  const measure = (fg, bg) => measureContrastPair(fg, bg, contrastAlgorithm, colorSpace);

  try {
    const bgColorData = resolveColorData(allColors, bgColor, _depth, contrastAlgorithm, colorSpace, resolvedById);
    const fgColorData = resolveColorData(allColors, fgColor, _depth, contrastAlgorithm, colorSpace, resolvedById);
    if (!bgColorData.isValid || !fgColorData.isValid) return invalidColor();

    const bgMeasureString = bgColorData.oklch
      ? formatOklchForMeasure(bgColorData.oklch)
      : (bgColorData.hex || '#808080');

    if (useAlpha) {
      const fgL = fgColorData.oklch.l;
      const fgC = fgColorData.oklch.c || 0;
      const fgH = fgColorData.oklch.h || 0;
      const bestAlpha = findOptimalAlpha(fgL, fgC, fgH, bgMeasureString, contrastLevel, measure);
      if (bestAlpha != null) {
        const emitted = emitAlphaColor(fgL, fgC, fgH, bestAlpha, bgMeasureString, contrastLevel, measure, colorSpace);
        if (emitted) return emitted;
      }
    }

    const whiteContrast = measure("#ffffff", bgMeasureString);
    const blackContrast = measure("#000000", bgMeasureString);
    const preferLighter = whiteContrast > blackContrast;

    const fgOklch = fgColorData.oklch;
    const targetHue = fgOklch?.h || 0;
    const relativeChroma = getRelativeChroma(fgOklch, colorSpace);

    const bgL = bgColorData.oklch?.l ?? 0.5;
    const limitedMinL = preferLighter ? bgL : 0.02;
    const limitedMaxL = preferLighter ? 0.98 : bgL;

    const tryRange = (minL, maxL) => {
      const L = findOptimalLightness(
        bgMeasureString,
        contrastLevel,
        relativeChroma,
        targetHue,
        minL,
        maxL,
        true,
        preferLighter,
        measure,
        colorSpace
      );
      if (L == null) return null;
      return emitLightnessColor(
        L,
        relativeChroma,
        targetHue,
        bgMeasureString,
        contrastLevel,
        preferLighter,
        measure,
        colorSpace,
        contrastAlgorithm
      );
    };

    const limited = tryRange(limitedMinL, limitedMaxL);
    if (limited) return limited;

    const full = tryRange(0.02, 0.98);
    if (full) return full;

    return extremeFallback(bgMeasureString, measure, colorSpace);
  } catch (error) {
    console.warn('Error calculating contrast color:', error);
    return extremeFallback('#808080', measure, colorSpace);
  }
}

/**
 * Get default placeholder values from reference color
 * @param {Object} referenceColor - Reference color object
 * @returns {Object} Placeholder values for L, C, H, A inputs
 */
export function getReferenceColorPlaceholders(referenceColor, workspaceGamut = 'p3') {
  if (!referenceColor || !referenceColor.oklch) {
    return {
      l: '0.5',
      c: '0.1',
      h: '240',
      a: '1'
    };
  }

  const oklch = referenceColor.oklch;

  // For C, show the reference color's relative chroma as a percentage,
  // since that's what will be applied when C is left empty but L/H/A change.
  const relativeChroma = getRelativeChroma(oklch, workspaceGamut);
  const relativeChromaPercent = Math.round((relativeChroma || 0) * 100);

  return {
    l: (oklch.l || 0).toFixed(3),
    c: `${relativeChromaPercent}%`,
    h: Math.round(oklch.h || 0).toString(),
    a: (oklch.alpha || 1).toFixed(3)
  };
}

/**
 * Validate and normalize LCHA expression
 * @param {string} expression - User input expression
 * @param {string} component - 'l', 'c', 'h', or 'a'
 * @returns {string|null} Normalized expression or null if invalid
 */
export function validateLCHAExpression(expression, component) {
  if (!expression || typeof expression !== 'string') {
    return null;
  }

  const trimmed = expression.trim();
  
  // For C component, allow simple percentage values (relative chroma)
  if (component === 'c') {
    // Allow simple percentage values like "70%" for relative chroma
    if (/^\d*\.?\d+%$/.test(trimmed)) {
      return trimmed;
    }
  }
  
  // For L component, allow percentage-based adjustments like "+10%", "-20%"
  if (component === 'l') {
    // Allow percentage expressions like "+10%", "-20%", "+50%"
    if (/^[+\-]\d*\.?\d+%$/.test(trimmed)) {
      return trimmed;
    }
  }
  
  // Allow simple numbers
  if (/^\d*\.?\d+$/.test(trimmed)) {
    return trimmed;
  }

  // Allow math expressions with +, -, *, /
  if (/^[+\-*/]?\d*\.?\d*([+\-*/]\d*\.?\d*)*$/.test(trimmed)) {
    return trimmed;
  }

  return null;
}
